/**
 * Internal logger for structured-data package
 * Uses @videoblocks/typescript-logger for consistent logging
 *
 * Note: This logger is for internal use only.
 * Consumers should import directly from '@videoblocks/typescript-logger'
 */
import { LoggerFactory } from '@videoblocks/typescript-logger';

type LogMeta = Record<string, unknown>;

export type LoggingConfig = {
    enabled: boolean;
    level: 'debug' | 'info' | 'warn' | 'error';
};

export interface Logger {
    debug(message: string, meta?: LogMeta): void;
    info(message: string, meta?: LogMeta): void;
    warn(message: string, meta?: LogMeta): void;
    error(message: string, meta?: LogMeta): void;
}

/**
 * Create a conditional logger based on provided configuration
 */
class ConditionalLogger implements Logger {
    private baseLogger = LoggerFactory.create('StructuredData');

    constructor(private config: LoggingConfig) {}

    debug(message: string, meta?: LogMeta): void {
        if (this.config.enabled && this.config.level === 'debug') {
            if (meta) {
                this.baseLogger.debug(meta, message);
            } else {
                this.baseLogger.debug(message);
            }
        }
    }

    info(message: string, meta?: LogMeta): void {
        if (this.config.enabled && ['debug', 'info'].includes(this.config.level)) {
            if (meta) {
                this.baseLogger.info(meta, message);
            } else {
                this.baseLogger.info(message);
            }
        }
    }

    warn(message: string, meta?: LogMeta): void {
        if (this.config.enabled && ['debug', 'info', 'warn'].includes(this.config.level)) {
            if (meta) {
                this.baseLogger.warn(meta, message);
            } else {
                this.baseLogger.warn(message);
            }
        }
    }

    error(message: string, meta?: LogMeta): void {
        if (this.config.enabled) {
            if (meta) {
                this.baseLogger.error(meta, message);
            } else {
                this.baseLogger.error(message);
            }
        }
    }

    /**
     * Update the logger configuration
     */
    updateConfig(newConfig: LoggingConfig): void {
        this.config = newConfig;
    }
}

/**
 * Factory function to create a logger with configuration
 */
export const createLogger = (config: LoggingConfig): Logger => {
    return new ConditionalLogger(config);
};

// Default logger instance with safe defaults (disabled by default)
const loggerInstance: ConditionalLogger = new ConditionalLogger({
    enabled: false,
    level: 'info',
});

/**
 * Update the default logger configuration
 * This allows config to initialize the logger without circular dependencies
 */
export const updateLoggerConfig = (config: LoggingConfig): void => {
    loggerInstance.updateConfig(config);
};

/**
 * Internal logger instance for the structured-data package
 */
export const logger: Logger = loggerInstance;
